import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import React, { useState, useMemo } from "react";
import {
  Table,
  Group,
  Text,
  ActionIcon,
  Select,
  ScrollArea,
  createStyles,
} from "@mantine/core";
import dayjs from "dayjs";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import { sortByField, SortDirection } from "../utils/sortUtils";
import usePagination from "../hooks/usePagination";
import StyledBadge from "../elements/StyledBadge";
import {
  getShiftLabel,
  formatTimeShort,
  parseTimeToMinutes,
  getStatusBadge,
  computeActualHours,
} from "../utils/attendanceUtils";

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 1,
  },
  sortableHeader: {
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme.colors.gray[0],
    },
  },
}));

interface EmployeeTableProps {
  records: EmployeeShiftRecord[];
  showDateColumn: boolean;
}

type SortField = "employee_name" | "date";

interface SortableHeaderProps {
  field: SortField;
  children: React.ReactNode;
  sortField: SortField;
  sortDirection: SortDirection;
  onToggleSort: (field: SortField) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  field,
  children,
  sortField,
  sortDirection,
  onToggleSort,
}) => {
  const { classes } = useStyles();

  return (
    <th className={classes.sortableHeader} onClick={() => onToggleSort(field)}>
      <Group spacing={4} noWrap>
        {children}
        {sortField === field &&
          (sortDirection === "asc" ? (
            <ChevronUp size={14} />
          ) : (
            <ChevronDown size={14} />
          ))}
      </Group>
    </th>
  );
};

const computeExpectedHours = (shiftStart: string, shiftEnd: string): number => {
  const startMin = parseTimeToMinutes(shiftStart);
  const endMin = parseTimeToMinutes(shiftEnd);
  if (startMin === null || endMin === null) return 0;
  let diff = endMin - startMin;
  if (diff <= 0) diff += 24 * 60; // overnight
  return Math.round((diff / 60) * 10) / 10;
};

const formatHours = (value: number): string =>
  value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  records,
  showDateColumn,
}) => {
  const { classes } = useStyles();
  const [sortField, setSortField] = useState<SortField>("employee_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const toggleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const fieldType = sortField === "date" ? "date" : "string";
  const sortedRecords = useMemo(
    () =>
      sortByField(records, {
        field: sortField,
        type: fieldType,
        direction: sortDirection,
      }),
    [records, sortField, fieldType, sortDirection]
  );

  const {
    page,
    rowsPerPage,
    totalRecords,
    totalPages,
    startIdx,
    endIdx,
    paginatedItems: paginatedRecords,
    handlePrevPage,
    handleNextPage,
    handleRowsPerPageChange,
  } = usePagination(sortedRecords);

  return (
    <div>
      <ScrollArea>
        <Table
          highlightOnHover
          striped
          sx={{ "& td, & th": { padding: "16px" } }}
        >
          <thead className={classes.header}>
            <tr>
              {showDateColumn && (
                <SortableHeader
                  field="date"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onToggleSort={toggleSort}
                >
                  <Text weight={600}>Date</Text>
                </SortableHeader>
              )}
              <SortableHeader
                field="employee_name"
                sortField={sortField}
                sortDirection={sortDirection}
                onToggleSort={toggleSort}
              >
                <Text weight={600}>Employee Name</Text>
              </SortableHeader>
              <th>
                <Text weight={600}>Employee ID</Text>
              </th>
              <th>
                <Text weight={600}>Shift</Text>
              </th>
              <th>
                <Text weight={600}>Time clocked in</Text>
              </th>
              <th>
                <Text weight={600}>Time clocked out</Text>
              </th>
              <th>
                <Text weight={600}>Expected Hours</Text>
              </th>
              <th>
                <Text weight={600}>Actual Hours</Text>
              </th>
              <th>
                <Text weight={600}>Status</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedRecords.map((record) => {
              const expectedHours = computeExpectedHours(
                record.shift_start,
                record.shift_end
              );
              const actualHours = computeActualHours(
                record.clock_in_time,
                record.clock_out_time
              );
              const statusBadge = getStatusBadge(record);

              return (
                <tr
                  key={`${record.employee_id}-${record.date}-${record.shift_start}-${record.shift_end}`}
                >
                  {showDateColumn && (
                    <td>
                      <Text size="sm">
                        {dayjs(record.date).format("D MMMM YYYY")}
                      </Text>
                    </td>
                  )}
                  <td>
                    <Text size="sm" weight={600}>
                      {record.employee_name}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">{record.employee_id}</Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {getShiftLabel(
                        record.shift_start,
                        record.shift_end
                      ).replace(" Shift", "")}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {record.clock_in_time
                        ? formatTimeShort(record.clock_in_time)
                        : "-"}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {record.clock_out_time
                        ? formatTimeShort(record.clock_out_time)
                        : "-"}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">{formatHours(expectedHours)}</Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {actualHours !== null ? formatHours(actualHours) : "-"}
                    </Text>
                  </td>
                  <td>
                    <Group spacing={4}>
                      {record.is_reliever ||
                      record.shift_code?.toUpperCase() === "O" ? (
                        <StyledBadge
                          variant="neutral"
                          label={
                            record.is_unscheduled
                              ? "Reliever (unscheduled)"
                              : "Reliever"
                          }
                        />
                      ) : (
                        record.is_unscheduled && (
                          <StyledBadge variant="neutral" label="Unscheduled" />
                        )
                      )}
                      {record.is_replacement && (
                        <StyledBadge
                          variant="replacement"
                          label="Replacement"
                        />
                      )}
                      <StyledBadge
                        variant={statusBadge.variant}
                        label={statusBadge.label}
                      />
                    </Group>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </ScrollArea>

      {totalRecords > 0 && (
        <Group position="apart" mt="md">
          <Group spacing="xs">
            <Text size="sm" color="dimmed">
              Rows per page:
            </Text>
            <Select
              data={[
                { value: "10", label: "10" },
                { value: "25", label: "25" },
                { value: "50", label: "50" },
              ]}
              value={String(rowsPerPage)}
              onChange={handleRowsPerPageChange}
              size="xs"
              sx={{ width: 70 }}
            />
          </Group>
          <Group spacing="xs">
            <Text size="sm" color="dimmed">
              {startIdx + 1}-{endIdx} of {totalRecords}
            </Text>
            <ActionIcon
              size="sm"
              variant="hover"
              onClick={handlePrevPage}
              disabled={page === 1}
            >
              <ChevronLeft size={16} />
            </ActionIcon>
            <ActionIcon
              size="sm"
              variant="hover"
              onClick={handleNextPage}
              disabled={page >= totalPages}
            >
              <ChevronRight size={16} />
            </ActionIcon>
          </Group>
        </Group>
      )}
    </div>
  );
};

export default EmployeeTable;
